import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const LanguageContext = createContext(null);

const DEFAULT_TRANSLATIONS = {
  // Navigation
  "nav.home": "Home",
  "nav.courses": "Courses",
  "nav.ebooks": "Ebooks",
  "nav.live_classes": "Live Classes",
  "nav.exams": "Exams",
  "nav.profile": "Profile",
  "nav.settings": "Settings",
  "nav.logout": "Logout",
  "nav.login": "Login",
  
  // Auth
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.login_title": "Welcome Back",
  "auth.sign_in": "Sign In",
  "auth.sign_up": "Sign Up",
  
  // Common
  "common.search": "Search",
  "common.loading": "Loading...",
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.free": "Free",
  
  // Messages
  "msg.welcome": "Welcome to Darasani",
  "msg.login_success": "Successfully logged in",
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [translations, setTranslations] = useState(DEFAULT_TRANSLATIONS);
  const [settings, setSettings] = useState({
    enabled_languages: ['en'],
    allow_user_change: true,
    auto_detect: true
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeLanguage();
  }, []);

  const initializeLanguage = async () => {
    try {
      // Load language settings
      const settingsRes = await axios.get(`${API}/languages/settings`);
      setSettings(settingsRes.data);

      // Check user preference in localStorage
      let preferredLang = localStorage.getItem('darasani_language');

      // If no preference and auto-detect is enabled, detect by IP
      if (!preferredLang && settingsRes.data.auto_detect) {
        try {
          const detectRes = await axios.get(`${API}/languages/detect-country`);
          preferredLang = detectRes.data.recommended_language;
        } catch (e) {
          console.log('Country detection failed:', e);
        }
      }

      // Fallback to default language
      if (!preferredLang || !settingsRes.data.enabled_languages.includes(preferredLang)) {
        preferredLang = settingsRes.data.default_language || 'en';
      }

      await changeLanguage(preferredLang);
    } catch (err) {
      console.error('Failed to initialize language:', err);
    } finally {
      setLoading(false);
    }
  };

  const changeLanguage = async (langCode) => {
    if (!settings.enabled_languages.includes(langCode) && langCode !== 'en') {
      console.warn(`Language ${langCode} is not enabled`);
      langCode = 'en';
    }

    try {
      const res = await axios.get(`${API}/languages/translations/${langCode}`);
      const newTranslations = { ...DEFAULT_TRANSLATIONS, ...res.data.translations };
      setTranslations(newTranslations);
      setLanguage(langCode);
      localStorage.setItem('darasani_language', langCode);
      document.documentElement.lang = langCode;
    } catch (err) {
      console.error('Failed to load translations:', err);
    }
  };

  // Translation function
  const t = (key, fallback = null) => {
    return translations[key] || fallback || key;
  };

  // Translation function with interpolation
  const tFormat = (key, params = {}) => {
    let text = translations[key] || key;
    Object.entries(params).forEach(([k, v]) => {
      text = text.replace(`{${k}}`, v);
    });
    return text;
  };

  return (
    <LanguageContext.Provider value={{
      language,
      translations,
      settings,
      loading,
      changeLanguage,
      t,
      tFormat,
      enabledLanguages: settings.enabled_languages,
      allowUserChange: settings.allow_user_change
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Language selector component
export const LanguageSelector = ({ className = '' }) => {
  const { language, changeLanguage, enabledLanguages, allowUserChange } = useLanguage();

  const LANGUAGES = {
    en: { name: "English", flag: "🇬🇧" },
    sw: { name: "Kiswahili", flag: "🇹🇿" },
    fr: { name: "Français", flag: "🇫🇷" },
    pt: { name: "Português", flag: "🇵🇹" },
    af: { name: "Afrikaans", flag: "🇿🇦" },
    zu: { name: "isiZulu", flag: "🇿🇦" },
  };

  if (!allowUserChange || enabledLanguages.length <= 1) {
    return null;
  }

  return (
    <select
      value={language}
      onChange={(e) => changeLanguage(e.target.value)}
      className={`bg-slate-800 border border-slate-700 rounded-lg py-2 px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${className}`}
    >
      {enabledLanguages.map(code => (
        <option key={code} value={code}>
          {LANGUAGES[code]?.flag} {LANGUAGES[code]?.name}
        </option>
      ))}
    </select>
  );
};

export default LanguageContext;
